import { valibotResolver } from '@hookform/resolvers/valibot'
import { useRouter } from '@tanstack/react-router'
import { Form, Input } from 'antd'
import { useAtom } from 'jotai'
import { useCallback, useEffect, type FC, type FormEvent } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { FaCheckCircle } from 'react-icons/fa'

import {
  BasicTable,
  BasicTbody,
  BasicTd,
  BasicTh,
  BasicTr,
  Button,
  ModalBody,
  ModalFooter,
  RequiredTag
} from '@/components/elements'
import {
  ACTIONS,
  ERROR_MESSAGES,
  LABELS,
  SUCCESS_MESSAGES
} from '@/constants/common/message.constants'
import { toastApiErrorMessage } from '@/features/shared/utils'
import {
  type CreatePackageCategoryDto,
  type PackageCategoryEntity
} from '@/libs/api'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'
import { initialPackageCategoryCreateFormValue } from '@/routes/_protected/$tenantKey/settings/package-categories/$packageCategoryId/-models'
import { CreatePackageCategoryDtoSchema } from '@/routes/_protected/$tenantKey/settings/package-categories/-modals'

import { createPackageCategory } from '../../api'

type Props = {
  tenantKey: string
  onSuccess: (packageCategory: PackageCategoryEntity) => Promise<void>
  onCancel: () => void
}

export const PackageCategoryCreateModalContent: FC<Props> = ({
  tenantKey,
  onSuccess,
  onCancel
}) => {
  const {
    control,
    setFocus,
    handleSubmit,
    formState: { isSubmitting, isValid }
  } = useForm<CreatePackageCategoryDto>({
    defaultValues: {
      ...initialPackageCategoryCreateFormValue,
      dcId: undefined
    },
    resolver: valibotResolver(CreatePackageCategoryDtoSchema)
  })

  const router = useRouter()
  const [selectedDcId] = useAtom(selectedDcIdAtom)

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      if (event) {
        if (typeof event.preventDefault === 'function') {
          event.preventDefault()
        }
        if (typeof event.stopPropagation === 'function') {
          event.stopPropagation()
        }
      }
      return handleSubmit(async (formValue) => {
        try {
          const { data } = await createPackageCategory({
            tenantKey,
            dto: {
              ...formValue,
              dcId: selectedDcId ?? undefined
            }
          })
          onSuccess(data)
          toast.success(SUCCESS_MESSAGES.REGISTERED_PACKAGE_CATEGORY)
        } catch (error) {
          toastApiErrorMessage(
            error,
            ERROR_MESSAGES.FAILED_TO_REGISTER_PACKAGE_CATEGORY
          )
        }
      })(event)
    },
    [handleSubmit, tenantKey, selectedDcId, onSuccess]
  )

  useEffect(() => {
    const unsubscribe = router.subscribe('onResolved', () => {
      onCancel()
    })
    return () => {
      unsubscribe()
    }
  }, [router, onCancel])

  useEffect(() => {
    setFocus('code')
  }, [setFocus])

  return (
    <form onSubmit={onSubmit}>
      <ModalBody>
        <BasicTable className="w-full table-fixed">
          <colgroup>
            <col className="w-[150px]" />
            <col />
          </colgroup>
          <BasicTbody>
            <BasicTr>
              <BasicTh className="align-top">
                <label htmlFor="code">
                  {LABELS.PACKAGE_CATEGORY_CODE}
                  <RequiredTag className="ml-2" />
                </label>
              </BasicTh>
              <BasicTd colSpan={3} className="align-top">
                <Controller
                  name={'code'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input
                        {...field}
                        placeholder={LABELS.PACKAGE_CATEGORY_CODE}
                        className="w-full"
                        id={field.name}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>
            <BasicTr>
              <BasicTh className="align-top">
                <label htmlFor="name">
                  {LABELS.PACKAGE_CATEGORY_NAME}
                  <RequiredTag className="ml-2" />
                </label>
              </BasicTh>
              <BasicTd colSpan={3} className="align-top">
                <Controller
                  name={'name'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input
                        {...field}
                        placeholder={LABELS.PACKAGE_CATEGORY_NAME}
                        className="w-full"
                        id={field.name}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>
            <BasicTr>
              <BasicTh className="align-top">
                <label htmlFor="shortName">
                  {LABELS.PACKAGE_CATEGORY_SHORT_NAME}
                </label>
                <RequiredTag className="ml-2" />
              </BasicTh>
              <BasicTd colSpan={3} className="align-top">
                <Controller
                  name={'shortName'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input
                        {...field}
                        placeholder={LABELS.PACKAGE_CATEGORY_SHORT_NAME}
                        className="w-full"
                        id={field.name}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>
          </BasicTbody>
        </BasicTable>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onCancel}>{ACTIONS.CANCEL}</Button>
        <Button
          type="primary"
          icon={<FaCheckCircle />}
          htmlType="submit"
          disabled={!isValid}
          loading={isSubmitting}
        >
          {ACTIONS.REGISTER}
        </Button>
      </ModalFooter>
    </form>
  )
}
